import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Building2, Package, Calendar, Bot,
  ChevronLeft, ChevronRight, CalendarClock, ClipboardList, LogOut, Users, FileText, ClipboardCheck
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Sidebar({ isOpen, setIsOpen, isMobile }) {
  const { t } = useLanguage();
  const { user, logout, isPatron } = useAuth();

  const navItems = [
    { path: "/", icon: LayoutDashboard, label: t('dashboard'), show: true },
    { path: "/chantiers", icon: Building2, label: t('chantiers'), show: true },
    { path: "/rdv", icon: CalendarClock, label: t('rdv'), show: isPatron() },
    { path: "/visites", icon: ClipboardCheck, label: "Visites", show: isPatron() },
    { path: "/demandes", icon: ClipboardList, label: t('demandes'), show: true },
    { path: "/notes", icon: FileText, label: t('notesChantierTitle'), show: isPatron() },
    { path: "/equipe", icon: Users, label: t('equipeTitle'), show: isPatron() },
    { path: "/materiaux", icon: Package, label: t('materiaux'), show: isPatron() },
    { path: "/calendrier", icon: Calendar, label: t('calendrier'), show: isPatron() },
    { path: "/assistant", icon: Bot, label: t('assistant'), show: isPatron() },
  ];

  if (isMobile) return null;

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed left-0 top-0 h-full bg-[#0F2744] border-r border-[#1A3557] z-50 transition-all duration-300 flex flex-col",
        isOpen ? "w-64" : "w-20"
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-[#1A3557]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-white flex items-center justify-center overflow-hidden shadow-md flex-shrink-0">
            <img src="/logo.png" alt="VGI Construct" className="w-full h-full object-contain" />
          </div>
          {isOpen && (
            <span className="font-['Barlow_Condensed'] font-bold text-xl tracking-tight text-white uppercase">
              VGI Construct
            </span>
          )}
        </div>
      </div>

      {/* User Info */}
      {user && isOpen && (
        <div className="px-4 py-3 border-b border-[#1A3557] bg-[#0A1F3A]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#3B82F6] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user.prenom?.[0]}{user.nom?.[0]}
            </div>
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate">{user.prenom} {user.nom}</p>
              <p className="text-slate-400 text-xs capitalize">{user.role === 'patron' ? '👔 Patron' : '👷 Ouvrier'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.filter(item => item.show).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
            end={item.path === '/'}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group",
                isActive
                  ? "bg-[#3B82F6] text-white shadow-lg shadow-blue-900/30"
                  : "text-slate-300 hover:bg-[#1A3557] hover:text-white"
              )
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {isOpen && <span className="font-medium text-sm truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="p-3 border-t border-[#1A3557] space-y-2">
        {/* Language Switcher */}
        {isOpen ? (
          <div className="flex items-center justify-between px-2">
            <span className="text-xs text-slate-400 uppercase tracking-wider">{t('language')}</span>
            <LanguageSwitcher />
          </div>
        ) : (
          <div className="flex justify-center">
            <LanguageSwitcher />
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={logout}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 w-full text-slate-300 hover:bg-red-900/20 hover:text-red-400",
            !isOpen && "justify-center"
          )}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {isOpen && <span className="font-medium text-sm">{t('logout')}</span>}
        </button>
      </div>

      {/* Toggle Button */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className="absolute -right-3 top-20 w-6 h-6 bg-[#3B82F6] rounded-full flex items-center justify-center text-white hover:bg-blue-500 transition-colors shadow-lg"
      >
        {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
    </aside>
  );
}
